import type {
  CompanyEquipmentPatch,
  EquipmentAdmission,
  EquipmentDigestFields,
  EquipmentLibraryBundle,
  EquipmentLibraryProduct,
  EquipmentMarketEvidence,
  EquipmentObservation,
  EquipmentPlanningBenchmark,
  EquipmentRevision,
  EquipmentSource,
  LegacyEquipmentIdLink,
} from '../../types/equipmentLibrary';
import {
  EQUIPMENT_LIBRARY_DIGEST_ALGORITHM,
  EQUIPMENT_LIBRARY_SCHEMA_VERSION,
} from '../../types/equipmentLibrary';
import {
  canonicalJson,
  hashCanonicalJson,
} from '../reportGeneration/reportContractHash';

export const EQUIPMENT_LIBRARY_DIGEST_DOMAIN = 'deep-atomic-equipment-library-v1' as const;

export const EQUIPMENT_LIBRARY_RECORD_KINDS = [
  'product',
  'revision',
  'source',
  'observation',
  'admission',
  'market-evidence',
  'company-patch',
  'legacy-id',
  'planning-benchmark',
  'bundle',
] as const;
export type EquipmentLibraryRecordKind = typeof EQUIPMENT_LIBRARY_RECORD_KINDS[number];

export type EquipmentDigestless<T extends EquipmentDigestFields> =
  T extends EquipmentDigestFields
    ? Omit<T, keyof EquipmentDigestFields>
    : never;

function withoutDigestFields(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const {
    digestAlgorithm: _digestAlgorithm,
    contentDigest: _contentDigest,
    ...payload
  } = value as Record<string, unknown>;
  return payload;
}

/**
 * Namespaced, deterministic digest. Object-key insertion order cannot affect
 * the result; array order remains significant because it is part of the frozen
 * record contract.
 */
export function equipmentLibraryRecordDigest(
  kind: EquipmentLibraryRecordKind,
  value: unknown,
): string {
  return hashCanonicalJson({
    domain: EQUIPMENT_LIBRARY_DIGEST_DOMAIN,
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    kind,
    payload: withoutDigestFields(value),
  });
}

export function canonicalEquipmentLibraryClone<T>(value: T): T {
  return JSON.parse(canonicalJson(value)) as T;
}

function deepFreeze<T>(value: T, seen = new Set<object>()): T {
  if (!value || typeof value !== 'object') return value;
  const object = value as object;
  if (seen.has(object)) return value;
  seen.add(object);
  for (const child of Object.values(object as Record<string, unknown>)) {
    deepFreeze(child, seen);
  }
  return Object.freeze(value);
}

/**
 * Canonically clones, digest-binds and recursively freezes one new record.
 * Callers pass the digestless payload; stale digest fields are ignored if an
 * object is deliberately re-sealed.
 */
export function sealEquipmentLibraryRecord<T extends object>(
  kind: EquipmentLibraryRecordKind,
  value: T,
): Readonly<T & EquipmentDigestFields> {
  const payload = canonicalEquipmentLibraryClone(withoutDigestFields(value)) as T;
  const sealed = {
    ...payload,
    digestAlgorithm: EQUIPMENT_LIBRARY_DIGEST_ALGORITHM,
    contentDigest: equipmentLibraryRecordDigest(kind, payload),
  } as T & EquipmentDigestFields;
  return deepFreeze(sealed);
}

export function verifyEquipmentLibraryRecordDigest(
  kind: EquipmentLibraryRecordKind,
  value: EquipmentDigestFields,
): boolean {
  return value.digestAlgorithm === EQUIPMENT_LIBRARY_DIGEST_ALGORITHM
    && /^[a-f\d]{64}$/.test(value.contentDigest)
    && equipmentLibraryRecordDigest(kind, value) === value.contentDigest;
}

export function sealEquipmentProduct(
  value: EquipmentDigestless<EquipmentLibraryProduct>,
): EquipmentLibraryProduct {
  return sealEquipmentLibraryRecord('product', value) as EquipmentLibraryProduct;
}

export function sealEquipmentRevision(
  value: EquipmentDigestless<EquipmentRevision>,
): EquipmentRevision {
  return sealEquipmentLibraryRecord('revision', value) as EquipmentRevision;
}

export function sealEquipmentSource(
  value: EquipmentDigestless<EquipmentSource>,
): EquipmentSource {
  return sealEquipmentLibraryRecord('source', value) as EquipmentSource;
}

export function sealEquipmentObservation(
  value: EquipmentDigestless<EquipmentObservation>,
): EquipmentObservation {
  return sealEquipmentLibraryRecord('observation', value) as EquipmentObservation;
}

export function sealEquipmentAdmission(
  value: EquipmentDigestless<EquipmentAdmission>,
): EquipmentAdmission {
  return sealEquipmentLibraryRecord('admission', value) as EquipmentAdmission;
}

export function sealEquipmentMarketEvidence(
  value: EquipmentDigestless<EquipmentMarketEvidence>,
): EquipmentMarketEvidence {
  return sealEquipmentLibraryRecord('market-evidence', value) as EquipmentMarketEvidence;
}

export function sealCompanyEquipmentPatch(
  value: EquipmentDigestless<CompanyEquipmentPatch>,
): CompanyEquipmentPatch {
  return sealEquipmentLibraryRecord('company-patch', value) as CompanyEquipmentPatch;
}

export function sealLegacyEquipmentIdLink(
  value: EquipmentDigestless<LegacyEquipmentIdLink>,
): LegacyEquipmentIdLink {
  return sealEquipmentLibraryRecord('legacy-id', value) as LegacyEquipmentIdLink;
}

export function sealEquipmentPlanningBenchmark(
  value: EquipmentDigestless<EquipmentPlanningBenchmark>,
): EquipmentPlanningBenchmark {
  return sealEquipmentLibraryRecord('planning-benchmark', value) as EquipmentPlanningBenchmark;
}

export function sealEquipmentLibraryBundle(
  value: EquipmentDigestless<EquipmentLibraryBundle>,
): EquipmentLibraryBundle {
  return sealEquipmentLibraryRecord('bundle', value) as EquipmentLibraryBundle;
}
